import React, { useCallback, useMemo } from 'react';
import { Modal, Form } from '@lucid/lego';
import type { FormField } from '@lucid/lego';

/**
 * Props for the RejectNotificationModal component
 */
export type RejectNotificationModalProps = {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Called with the selected rejection reason on submit */
  onSubmit: (reason: string) => void;
  /** Whether the reject API call is in flight */
  isPending?: boolean;
  /** Theme variant */
  theme?: 'light' | 'dark';
  /** Test ID for the modal */
  dataTestId: string;
  /** i18n labels */
  labels?: Record<string, string>;
};

/**
 * Modal with a rejection reason form for rejecting a vehicle order notification.
 * @component
 * @param props.isOpen - Whether the modal is open
 * @param props.onClose - Callback to close the modal
 * @param props.onSubmit - Called with the selected rejection reason on submit
 * @param props.isPending - Whether the reject API call is pending
 * @param props.theme - Theme variant
 * @param props.dataTestId - Test ID
 * @returns {JSX.Element | null}
 * @example
 * ```tsx
 * <RejectNotificationModal
 *   dataTestId="reject-modal"
 *   isOpen={isOpen}
 *   onClose={closeModal}
 *   onSubmit={(reason) => rejectOrder({ reason })}
 *   theme="dark"
 * />
 * ```
 */
const RejectNotificationModal: React.FC<RejectNotificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isPending = false,
  theme = 'dark',
  dataTestId,
  labels,
}) => {
  const handleSuccess = useCallback(
    (data: unknown) => {
      const { reason } = data as { reason: string };
      onSubmit(reason);
    },
    [onSubmit]
  );

  const REJECTION_REASON_OPTIONS = useMemo(
    () => [
      {
        value: 'Configuration mismatch',
        label: labels?.['modal.configurationMismatch'] ?? 'Configuration mismatch',
      },
      { value: 'Higher cost', label: labels?.['modal.higherCost'] ?? 'Higher cost' },
      { value: 'Delivery delay', label: labels?.['modal.deliveryDelay'] ?? 'Delivery delay' },
      {
        value: 'Location constraint',
        label: labels?.['modal.locationConstraint'] ?? 'Location constraint',
      },
      { value: 'Other', label: labels?.['modal.other'] ?? 'Other' },
    ],
    [labels]
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        id: 'reason',
        fieldType: 'dropdown',
        name: 'reason',
        apiKey: 'reason',
        label: labels?.['modal.reasonForRejection'] ?? 'Reason for rejection',
        placeholder: labels?.['modal.aReasonPlaceholder'] ?? 'Select a reason',
        required: true,
        width: 'full',
        options: REJECTION_REASON_OPTIONS,
        validationMessages: {
          required: labels?.['modal.pleaseSelectA'] ?? 'Please select a reason for rejection',
        },
      },
      {
        id: 'buttonGroup',
        fieldType: 'buttonGroup',
        align: 'right',
        buttons: [
          {
            id: 'cancel',
            label: labels?.['modal.cancel'] ?? 'CANCEL',
            type: 'secondary',
            color: 'neutral',
            size: 'medium',
            isFluid: true,
            disabled: isPending,
            onClick: () => onClose(),
          },
          {
            id: 'submit',
            label: isPending
              ? (labels?.['modal.submitting'] ?? 'SUBMITTING...')
              : (labels?.['modal.submit'] ?? 'SUBMIT'),
            type: 'primary',
            color: 'neutral',
            size: 'medium',
            isFluid: true,
            disabled: isPending,
          },
        ],
      },
    ],
    [onClose, isPending, labels, REJECTION_REASON_OPTIONS]
  );

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={labels?.['modal.rejectVehicle'] ?? 'Reject Vehicle'}
      theme={theme}
      size="small"
      closeOnOverlayClick={!isPending}
    >
      <Form
        id="reject-notification-form"
        theme={theme}
        formFields={formFields}
        onSuccess={handleSuccess}
        isFluid
        alignment="left"
      />
    </Modal>
  );
};

export default RejectNotificationModal;
